import React, { useEffect, useState, useRef, useMemo } from "react"
import {
	Combobox,
	ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions
} from "@headlessui/react"
import { RxCaretSort } from "react-icons/rx"
import { IoMdCheckmark } from "react-icons/io"
import { PiWarningCircleFill } from "react-icons/pi"
import { getCurrentTime } from "../../../../utils/generalUtils"
import { useSelector } from "react-redux"
export default function DropdownCombobox({
	label,
	options,
	value,
	onChange,
	displayKey = "name",
	valueKey = "id",
	calculateAvailability,
	time,
	newPatientStart,
	newPatientValue,
	newPatientMRN,
	maxAcuity = 15,
	validationError,
	defaultValue,
	shouldDisplayUpward = false,
	readOnly = false,
	onFocus,
	onBlur,
	selectTextOnFocus = false
}) {
	const [query, setQuery] = useState("")
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)

	const inputRef = useRef(null)

	// Initialize the selected value with the defaultValue if no value is provided
	useEffect(() => {
		if (!value && defaultValue) {
			onChange(defaultValue)
		}
	}, [value, defaultValue, onChange])

	const [isAvailable, setIsAvailable] = useState([])

	useEffect(() => {
		if (!calculateAvailability) return

		const currentTime = time ? time : getCurrentTime(sandbox, isTomorrowMode)
		const availability = calculateAvailability(
			options,
			newPatientStart ? newPatientStart : currentTime,
			newPatientValue,
			newPatientMRN,
			maxAcuity,
			sandbox,
			isTomorrowMode
		)

		// Improved array comparison
		setIsAvailable(prevAvailability => {
			// If lengths are different, update state
			if (!prevAvailability || prevAvailability.length !== availability.length) {
				return availability
			}

			// Compare each element
			const hasChanged = availability.some((val, idx) => val !== prevAvailability[idx])
			return hasChanged ? availability : prevAvailability
		})
		// `newPatientStart` is intentionally omitted to avoid recomputing availability when only the start time string updates.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		options,
		newPatientValue,
		maxAcuity,
		time,
		newPatientMRN,
		calculateAvailability,
		sandbox,
		isTomorrowMode
	])

	const filteredOptions = useMemo(() => {
		if (query === "") return options
		const lowered = query.toLowerCase()
		return options.filter(option => {
			const displayValue =
				typeof displayKey === "function" ? displayKey(option) : option[displayKey]
			return displayValue?.toLowerCase().includes(lowered)
		})
	}, [options, query, displayKey])

	// Cache the currently-selected option so we don't re-run options.find on
	// every render — significant when options is long.
	const selectedOption = useMemo(
		() => options.find(option => option[valueKey] === value) || null,
		[options, value, valueKey]
	)

	// Index availability by option key so the render loop doesn't call
	// options.indexOf per row (O(N) per row -> O(N^2) total).
	const availabilityByKey = useMemo(() => {
		const map = new Map()
		options.forEach((option, idx) => {
			map.set(option[valueKey], isAvailable[idx])
		})
		return map
	}, [options, isAvailable, valueKey])

	// Use useEffect to manage body overflow
	useEffect(() => {
		if (isDropdownOpen) {
			document.body.style.overflow = "hidden" // Prevent body scroll
		} else {
			document.body.style.overflow = "" // Restore body scroll
		}
		return () => {
			document.body.style.overflow = "" // Clean up on unmount
		}
	}, [isDropdownOpen])

	useEffect(() => {
		const inputEl = inputRef.current
		if (selectTextOnFocus && inputEl) {
			const handleFocus = () => {
				inputEl.select()
			}

			inputEl.addEventListener("focus", handleFocus)

			return () => {
				inputEl.removeEventListener("focus", handleFocus)
			}
		}
	}, [selectTextOnFocus])

	return (
		<div className={`relative mt-2 ${readOnly ? "opacity-50" : ""}`}>
			<Combobox
				as="div"
				value={selectedOption}
				onChange={selectedOption => {
					if (!readOnly) {
						setQuery("")
						onChange(selectedOption ? selectedOption[valueKey] : null)
					}
					setIsDropdownOpen(false) // Close dropdown on selection
				}}
				onToggle={() => setIsDropdownOpen(prev => !prev)} // Toggle dropdown open state
				disabled={readOnly}
			>
				<div className="relative">
					<ComboboxInput
						className={`mt-1 block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
							validationError
								? "text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
								: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
						}`}
						onChange={event => setQuery(event.target.value)}
						displayValue={option =>
							option
								? typeof displayKey === "function"
									? displayKey(option)
									: option[displayKey]
								: ""
						}
						placeholder={label}
						aria-invalid={validationError ? "true" : "false"}
						aria-describedby={validationError ? `${valueKey}-error` : undefined}
						readOnly={readOnly}
						onFocus={onFocus}
						onBlur={onBlur}
						ref={inputRef}
					/>

					<ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
						{validationError && (
							<PiWarningCircleFill
								aria-hidden="true"
								className="h-5 w-5 text-red-500 mr-1"
							/>
						)}
						<RxCaretSort className="h-5 w-5 text-gray-400" aria-hidden="true" />
					</ComboboxButton>

					{filteredOptions && filteredOptions.length > 0 && (
						<ComboboxOptions
							className={`absolute z-50 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${
								shouldDisplayUpward ? "bottom-full mb-1" : "top-full mt-1"
							}`}
						>
							{filteredOptions.map((option, index) => {
								const available = availabilityByKey.get(option[valueKey])
								const displayValue =
									typeof displayKey === "function"
										? displayKey(option)
										: option[displayKey]

								return (
									<ComboboxOption
										key={option[valueKey]}
										value={option}
										className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-custom-blue data-[focus]:text-white"
									>
										<span className="block truncate group-data-[selected]:font-semibold">
											{calculateAvailability &&
												(available ? (
													<span className="inline-block w-2 h-2 mr-2 bg-green-500 rounded-full"></span>
												) : (
													<span className="inline-block w-2 h-2 mr-2 bg-gray-400 rounded-full"></span>
												))}
											{displayValue}
										</span>

										<span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-custom-blue group-data-[selected]:flex group-data-[focus]:text-white">
											<IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
										</span>
									</ComboboxOption>
								)
							})}
						</ComboboxOptions>
					)}
				</div>
			</Combobox>
			{validationError && (
				<p id={`${valueKey}-error`} className="mt-2 text-sm text-red-600">
					{validationError}
				</p>
			)}
		</div>
	)
}
